import { t } from "@lingui/macro";

import { USD_DECIMALS } from "config/factors";
import {
  selectChainId,
  selectPositionsInfoData,
  selectTokensData,
} from "context/SyntheticsStateContext/selectors/globalSelectors";
import { selectShowPnlAfterFees } from "context/SyntheticsStateContext/selectors/settingsSelectors";
import { selectTradeboxToTokenAddress } from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { createSelector } from "context/SyntheticsStateContext/utils";
import { getTokenData } from "domain/synthetics/tokens";
import { formatAmount, formatDeltaUsd } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { convertTokenAddress, getPriceDecimals } from "sdk/configs/tokens";
import { getMarketPoolName } from "sdk/utils/markets";

import { StaticChartLine } from "components/TVChartContainer/types";

import { selectChartToken } from ".";

export const selectChartLines = createSelector<StaticChartLine[]>((q) => {
  const chainId = q(selectChainId);
  const { chartToken } = q(selectChartToken);
  const positionsInfo = q(selectPositionsInfoData);
  const showPnlAfterFees = q(selectShowPnlAfterFees);

  const chartTokenAddress = q(selectTradeboxToTokenAddress) ?? chartToken?.address;

  if (!chartTokenAddress) {
    return EMPTY_ARRAY;
  }

  const filteredPositions = Object.values(positionsInfo || {}).filter(
    (position) =>
      position.marketInfo &&
      convertTokenAddress(chainId, position.marketInfo.indexTokenAddress, "wrapped") ===
        convertTokenAddress(chainId, chartTokenAddress, "wrapped")
  );

  const positionLines = filteredPositions.flatMap((position) => {
    const priceDecimal = getPriceDecimals(chainId, position.indexToken.symbol);
    const longOrShortText = position.isLong ? t`Long` : t`Short`;
    const sideLabel = position.isLong ? "B" : "S";
    const token = q((state) => getTokenData(selectTokensData(state), position.marketInfo?.indexTokenAddress, "native"));
    const marketPoolName =
      position.poolName ??
      getMarketPoolName({
        longToken: position.longToken,
        shortToken: position.shortToken,
      });
    const tokenVisualMultiplier = token?.visualMultiplier;
    const displayedPnl = showPnlAfterFees ? position.pnlAfterFees : position.pnl;
    const displayedPnlPercentage = showPnlAfterFees ? position.pnlAfterFeesPercentage : position.pnlPercentage;

    let quantityTextColor = "#b7bac7";
    if (displayedPnl > 0n) {
      quantityTextColor = "#00e39d";
    } else if (displayedPnl < 0n) {
      quantityTextColor = "#ff5f7c";
    }

    const lines: StaticChartLine[] = [
      {
        id: position.key,
        title: `${sideLabel} - ${t`Open ${longOrShortText} - ${marketPoolName}`}`,
        price: parseFloat(
          formatAmount(position.entryPrice, USD_DECIMALS, priceDecimal, undefined, undefined, tokenVisualMultiplier)
        ),
        quantity: formatDeltaUsd(displayedPnl, displayedPnlPercentage),
        lineColor: "#6d7388",
        quantityTextColor,
        quantityBackgroundColor: "#111315",
        quantityBorderColor: "#6d7388",
      },
    ];

    return lines;
  });

  return positionLines;
});
